import React, { useEffect, useState, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  CircularProgress,
  Alert,
  MenuItem,
  Skeleton,
  TextField,
  Tooltip,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { ApiClient, ApiError } from '../services/api';
import { env } from '../config/env';
import { useAuth } from '../contexts/AuthContext';
import { usePartner } from '../contexts/PartnerContext';
import { usePartnerScope } from '../contexts/PartnerScopeContext';
import { SelectPartnerPrompt } from '../components/SelectPartnerPrompt';

// Sophia design tokens
const PURPLE = '#250e62';
const LIGHT_COOL_GREY = '#EFF3F5';
const MEDIUM_GREY = '#C5CDD2';
const DARK_GREY = '#6D6E71';

// Set Highcharts global options
Highcharts.setOptions({
  chart: {
    style: {
      fontFamily:
        '"Jost", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    },
  },
  colors: [PURPLE],
  credits: { enabled: false },
});

interface StatCard {
  label: string;
  value: number | null;
  icon: string;
  /** Definition of what the number counts, shown on hover */
  tooltip?: string;
  /** Short definition rendered under the label */
  sublabel?: string;
  /** Render a skeleton (request still in flight) instead of "--" when value is null */
  loading?: boolean;
}

export function Dashboard(): React.ReactElement {
  const { idToken } = useAuth();
  const { activePartnerId: partnerId } = usePartnerScope();
  const { institutionName, logoUrl } = usePartner();
  const today = useMemo(() => dayjs(), []);
  const CUSTOM_RANGE = -1;
  const PARTNER_ID = partnerId ?? 0;
  const api = useMemo(() => new ApiClient(idToken, env.transcriptApiUrl), [idToken]);
  const studentApi = useMemo(() => new ApiClient(idToken, env.studentApiUrl), [idToken]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Stat card data
  const [totalTranscripts, setTotalTranscripts] = useState<number | null>(null);
  const [uniqueStudents, setUniqueStudents] = useState<number | null>(null);

  // Chart data
  const [monthlyData, setMonthlyData] = useState<{ month: string; label: string; count: number }[]>(
    []
  );
  const [topCourses, setTopCourses] = useState<{ name: string; count: number }[]>([]);
  const [sophiaCourses, setSophiaCourses] = useState<
    { name: string; count: number; articulated: boolean }[]
  >([]);

  // Student metrics
  const [activeStudents, setActiveStudents] = useState<number | null>(null);
  const [activeStudentsLoading, setActiveStudentsLoading] = useState(true);

  const [dateFrom, setDateFrom] = useState<Dayjs | null>(() =>
    today.startOf('month').subtract(11, 'month')
  );
  const [dateTo, setDateTo] = useState<Dayjs | null>(() => today);
  // Course Trend preset selector value; a preset writes into the range above. -1 = custom range.
  const [trendPreset, setTrendPreset] = useState<number>(12);
  const [downloadingCsv, setDownloadingCsv] = useState(false);

  // Course chart date ranges (0 = all time)
  const [partnerCourseMonths, setPartnerCourseMonths] = useState(12);
  const [sophiaCourseMonths, setSophiaCourseMonths] = useState(12);

  // Course chart show count (0 = all)
  const [partnerCourseLimit, setPartnerCourseLimit] = useState(10);
  const [sophiaCourseLimit, setSophiaCourseLimit] = useState(10);

  // Initial load — stats, trend, and student data
  useEffect(() => {
    if (partnerId == null) return;
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        setError(null);
        setActiveStudentsLoading(true);

        // Student API — fire and forget so it doesn't block the dashboard spinner,
        // started before the awaited calls so a transcript API failure can't strand
        // the Active Students tile on its skeleton
        studentApi
          .getStudentProgress(PARTNER_ID, { per_page: 1 })
          .then((progressRes) => setActiveStudents(progressRes.summary.active_students))
          .catch((studentErr) => console.warn('[Dashboard] Student API unavailable:', studentErr))
          .finally(() => setActiveStudentsLoading(false));

        const statsRes = await api.getTranscriptStats(PARTNER_ID);

        setTotalTranscripts(statsRes.total_transcripts);
        setUniqueStudents(statsRes.unique_students);
      } catch (err) {
        console.error('[Dashboard] Failed to fetch data:', err);
        // A 403 means the user's roles have no dashboard data access
        // (e.g. api_developer only) — show the empty state, not the raw
        // backend role list
        if (!(err instanceof ApiError && err.status === 403)) {
          setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [partnerId]);

  const trendFetchMonths = useMemo(() => {
    if (!dateFrom) return 12;
    return Math.max(1, today.startOf('month').diff(dateFrom.startOf('month'), 'month') + 1);
  }, [dateFrom, today]);

  useEffect(() => {
    if (partnerId == null) return;
    api
      .getTranscriptTrend(PARTNER_ID, trendFetchMonths)
      .then((res) =>
        setMonthlyData(res.items.map((m) => ({ month: m.month, label: m.label, count: m.count })))
      )
      .catch((err) => console.error('[Dashboard] Trend failed:', err));
  }, [trendFetchMonths, partnerId]);

  const filteredTrend = useMemo(
    () =>
      monthlyData.filter((m) => {
        const d = dayjs(m.month);
        if (dateFrom && d.isBefore(dateFrom, 'month')) return false;
        if (dateTo && d.isAfter(dateTo, 'month')) return false;
        return true;
      }),
    [monthlyData, dateFrom, dateTo]
  );

  // Partner course chart — re-fetches when date range or limit changes
  useEffect(() => {
    if (partnerId == null) return;
    api
      .getTranscriptCourseCounts(PARTNER_ID, true, partnerCourseMonths || undefined)
      .then((res) => {
        const items = partnerCourseLimit ? res.items.slice(0, partnerCourseLimit) : res.items;

        setTopCourses(items.map((c) => ({ name: c.title, count: c.count })));
      })
      .catch((err) => console.error('[Dashboard] Partner courses failed:', err));
  }, [partnerCourseMonths, partnerCourseLimit, partnerId]);

  // Sophia course chart — re-fetches when date range or limit changes
  useEffect(() => {
    if (partnerId == null) return;
    api
      .getSophiaCourseCounts(PARTNER_ID, sophiaCourseMonths || undefined)
      .then((res) => {
        const items = sophiaCourseLimit ? res.items.slice(0, sophiaCourseLimit) : res.items;
        setSophiaCourses(
          items.map((c) => ({
            name: c.title,
            count: c.count,
            articulated: c.articulated,
          }))
        );
      })
      .catch((err) => console.error('[Dashboard] Sophia courses failed:', err));
  }, [sophiaCourseMonths, sophiaCourseLimit, partnerId]);

  const handleDownloadCsv = async () => {
    if (!dateFrom || !dateTo || !totalTranscripts) return;
    try {
      setDownloadingCsv(true);
      const from = dateFrom.format('YYYY-MM-DD');
      const to = dateTo.format('YYYY-MM-DD');
      // Server-side export: one request instead of paging through the list
      // endpoint, which tripped the LB rate limit at scale (SOPHIA-35098)
      const blob = await api.downloadTranscriptExport(PARTNER_ID, {
        date_from: from,
        date_to: to,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `sophia-course-completions-${today}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[Dashboard] CSV export failed:', err);
      setError(err instanceof Error ? err.message : 'CSV export failed');
    } finally {
      setDownloadingCsv(false);
    }
  };

  const applyTrendPreset = (months: number): void => {
    setTrendPreset(months);
    setDateFrom(today.startOf('month').subtract(months - 1, 'month'));
    setDateTo(today);
  };

  const stats: StatCard[] = [
    {
      label: 'Active Students',
      value: activeStudents,
      icon: 'fa-thin fa-user-plus',
      sublabel: 'with at least one course in progress',
      tooltip: 'Students with at least one course currently in progress',
      loading: activeStudentsLoading,
    },
    {
      label: 'Students with Transcripts',
      value: uniqueStudents,
      icon: 'fa-thin fa-users',
      sublabel: 'all time',
      tooltip: 'Unique students with at least one completed course, all time',
    },
    {
      label: 'Completed Courses',
      value: totalTranscripts,
      icon: 'fa-thin fa-file-lines',
      sublabel: 'all time',
      tooltip: 'Completed courses (transcripts issued), all time',
    },
  ];

  // --- Chart configs ---

  const courseChartOptions = (
    categories: string[],
    data: Highcharts.PointOptionsObject[],
    legend: boolean
  ): Highcharts.Options => ({
    chart: {
      type: 'bar',
      height: Math.max(200, categories.length * 28 + 60 + (legend ? 40 : 0)),
      backgroundColor: 'transparent',
    },
    title: { text: undefined },
    xAxis: {
      categories,
      lineColor: MEDIUM_GREY,
      labels: { style: { color: DARK_GREY, fontSize: '12px' } },
    },
    yAxis: {
      title: { text: 'Enrollments', style: { color: DARK_GREY, fontSize: '12px' } },
      allowDecimals: false,
      gridLineColor: LIGHT_COOL_GREY,
      labels: { style: { color: DARK_GREY, fontSize: '12px' } },
    },
    legend: legend
      ? {
          enabled: true,
          itemStyle: { color: DARK_GREY, fontSize: '11px', fontWeight: '400' },
          symbolRadius: 2,
        }
      : { enabled: false },
    tooltip: { pointFormat: '<b>{point.y}</b> enrollments' },
    plotOptions: {
      bar: { borderRadius: 2, borderWidth: 0, pointWidth: 6 },
    },
    series: [
      {
        type: 'bar',
        name: 'Enrollments',
        data,
        showInLegend: false,
      },
      ...(legend
        ? [
            {
              type: 'scatter' as const,
              name: 'Articulated',
              color: PURPLE,
              data: [] as number[],
              showInLegend: true,
              marker: { symbol: 'square', radius: 5 },
            },
            {
              type: 'scatter' as const,
              name: 'Not yet articulated',
              color: MEDIUM_GREY,
              data: [] as number[],
              showInLegend: true,
              marker: { symbol: 'square', radius: 5 },
            },
          ]
        : []),
    ],
  });

  const topCoursesChartOptions = courseChartOptions(
    topCourses.map((c) => c.name),
    topCourses.map((c) => ({ y: c.count, color: PURPLE })),
    false
  );

  const sophiaCoursesChartOptions = courseChartOptions(
    sophiaCourses.map((c) => c.name),
    sophiaCourses.map((c) => ({ y: c.count, color: c.articulated ? PURPLE : MEDIUM_GREY })),
    true
  );

  const trendChartOptions: Highcharts.Options = {
    chart: { type: 'areaspline', height: 360, backgroundColor: 'transparent' },
    title: { text: undefined },
    xAxis: {
      categories: filteredTrend.map((d) => d.label),
      lineColor: MEDIUM_GREY,
      tickColor: MEDIUM_GREY,
      labels: { style: { color: DARK_GREY, fontSize: '12px' } },
    },
    yAxis: {
      title: { text: undefined },
      gridLineColor: LIGHT_COOL_GREY,
      labels: { style: { color: DARK_GREY, fontSize: '12px' } },
    },
    legend: { enabled: false },
    tooltip: {
      headerFormat: '<b>{point.key}</b><br/>',
      pointFormat: '{point.y} courses',
    },
    plotOptions: {
      areaspline: {
        fillOpacity: 0,
        lineWidth: 2.5,
        marker: {
          enabled: true,
          radius: 4,
          fillColor: PURPLE,
          lineColor: '#fff',
          lineWidth: 2,
        },
      },
    },
    series: [
      {
        type: 'areaspline',
        name: 'Courses',
        data: filteredTrend.map((d) => d.count),
        color: PURPLE,
      },
    ],
  };

  // --- Shared card style ---
  const cardSx = {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 2,
    p: 2.5,
  };

  if (partnerId == null) {
    return <SelectPartnerPrompt />;
  }

  return (
    <>
      {/* Page header — cool grey band */}
      <Box sx={{ bgcolor: 'background.default' }}>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          {logoUrl && (
            <Box
              component="img"
              src={logoUrl}
              alt={institutionName ?? 'Partner'}
              sx={{ height: 60, objectFit: 'contain', display: 'block' }}
            />
          )}
        </Container>
      </Box>

      {/* Content area — white background */}
      <Box sx={{ bgcolor: 'background.paper', flex: 1 }}>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Stat cards */}
              <Grid container spacing={2.5}>
                {stats.map((stat) => (
                  <Grid item xs={12} md={4} key={stat.label}>
                    <Tooltip title={stat.tooltip ?? ''} placement="top" arrow>
                      <Box
                        sx={{
                          ...cardSx,
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 2,
                        }}
                      >
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '50%',
                            bgcolor: LIGHT_COOL_GREY,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <i className={stat.icon} style={{ fontSize: 20, color: PURPLE }} />
                        </Box>
                        <Box>
                          <Typography
                            variant="h4"
                            sx={{ fontWeight: 700, color: PURPLE, lineHeight: 1.1 }}
                          >
                            {stat.value !== null ? (
                              stat.value.toLocaleString()
                            ) : stat.loading ? (
                              <Skeleton width={80} data-testid={`${stat.label}-skeleton`} />
                            ) : (
                              '--'
                            )}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5, fontSize: '0.8125rem' }}
                          >
                            {stat.label}
                          </Typography>
                          {stat.sublabel && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', fontSize: '0.6875rem' }}
                            >
                              {stat.sublabel}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Tooltip>
                  </Grid>
                ))}
              </Grid>

              {/* Course date range — drives BOTH the CSV export and the Course Trend graph */}
              <Box
                sx={{
                  mt: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  Course date range
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="From"
                    value={dateFrom}
                    maxDate={dateTo ?? today}
                    disableFuture
                    onChange={(val) => {
                      setDateFrom(val);
                      setTrendPreset(CUSTOM_RANGE);
                    }}
                    slots={{
                      openPickerIcon: () => (
                        <i className="fa-thin fa-calendar" style={{ fontSize: 18 }} />
                      ),
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: {
                          width: 170,
                          '& .MuiOutlinedInput-root': { borderRadius: 5 },
                        },
                      },
                    }}
                  />
                  <DatePicker
                    label="To"
                    value={dateTo}
                    minDate={dateFrom ?? undefined}
                    maxDate={today}
                    disableFuture
                    onChange={(val) => {
                      setDateTo(val);
                      setTrendPreset(CUSTOM_RANGE);
                    }}
                    slots={{
                      openPickerIcon: () => (
                        <i className="fa-thin fa-calendar" style={{ fontSize: 18 }} />
                      ),
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: {
                          width: 170,
                          '& .MuiOutlinedInput-root': { borderRadius: 5 },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
                <Tooltip
                  title={
                    downloadingCsv
                      ? 'Exporting...'
                      : !dateFrom || !dateTo
                        ? 'Select a date range first'
                        : 'Export transcripts as CSV'
                  }
                >
                  <Typography
                    component="span"
                    variant="body2"
                    onClick={!downloadingCsv && dateFrom && dateTo ? handleDownloadCsv : undefined}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color:
                        !downloadingCsv && dateFrom && dateTo ? 'primary.main' : 'text.disabled',
                      fontWeight: 600,
                      cursor: !downloadingCsv && dateFrom && dateTo ? 'pointer' : 'default',
                      whiteSpace: 'nowrap',
                      '&:hover':
                        !downloadingCsv && dateFrom && dateTo
                          ? { textDecoration: 'underline' }
                          : {},
                    }}
                  >
                    {downloadingCsv ? (
                      <CircularProgress size={16} />
                    ) : (
                      <i className="fa-thin fa-file-arrow-down" style={{ fontSize: 18 }} />
                    )}
                    Export CSV
                  </Typography>
                </Tooltip>
              </Box>

              {/* Transcript trend chart */}
              <Box sx={{ ...cardSx, mt: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, color: PURPLE }}>
                    Course Trend
                  </Typography>
                  <TextField
                    select
                    size="small"
                    value={trendPreset}
                    onChange={(e) => {
                      const n = Number(e.target.value);
                      if (n !== CUSTOM_RANGE) applyTrendPreset(n);
                    }}
                    sx={{ minWidth: 160 }}
                  >
                    {trendPreset === CUSTOM_RANGE && (
                      <MenuItem value={CUSTOM_RANGE} disabled>
                        Custom range
                      </MenuItem>
                    )}
                    <MenuItem value={3}>Last 3 months</MenuItem>
                    <MenuItem value={6}>Last 6 months</MenuItem>
                    <MenuItem value={12}>Last 12 months</MenuItem>
                    <MenuItem value={24}>Last 2 years</MenuItem>
                    <MenuItem value={36}>Last 3 years</MenuItem>
                  </TextField>
                </Box>
                <HighchartsReact highcharts={Highcharts} options={trendChartOptions} />
              </Box>

              {/* Course popularity charts */}
              <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
                <Grid item xs={12} md={6}>
                  <Box sx={cardSx}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 2,
                        gap: 1,
                      }}
                    >
                      <Box sx={{ flexShrink: 1, minWidth: 0 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: PURPLE }}>
                          Most Popular Courses
                        </Typography>
                        {institutionName && (
                          <Typography variant="body2" sx={{ color: DARK_GREY, mt: 0.25 }}>
                            At {institutionName}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        <TextField
                          select
                          size="small"
                          value={partnerCourseLimit}
                          onChange={(e) => setPartnerCourseLimit(Number(e.target.value))}
                          sx={{ minWidth: 90 }}
                        >
                          <MenuItem value={10}>Top 10</MenuItem>
                          <MenuItem value={25}>Top 25</MenuItem>
                          <MenuItem value={0}>All</MenuItem>
                        </TextField>
                        <TextField
                          select
                          size="small"
                          value={partnerCourseMonths}
                          onChange={(e) => setPartnerCourseMonths(Number(e.target.value))}
                          sx={{ minWidth: 140 }}
                        >
                          <MenuItem value={0}>All time</MenuItem>
                          <MenuItem value={3}>Last 3 months</MenuItem>
                          <MenuItem value={6}>Last 6 months</MenuItem>
                          <MenuItem value={12}>Last 12 months</MenuItem>
                          <MenuItem value={24}>Last 2 years</MenuItem>
                        </TextField>
                      </Box>
                    </Box>
                    <HighchartsReact highcharts={Highcharts} options={topCoursesChartOptions} />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={cardSx}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 2,
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: PURPLE, flexShrink: 1, minWidth: 0 }}
                      >
                        Most Popular Courses at Sophia
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        <TextField
                          select
                          size="small"
                          value={sophiaCourseLimit}
                          onChange={(e) => setSophiaCourseLimit(Number(e.target.value))}
                          sx={{ minWidth: 90 }}
                        >
                          <MenuItem value={10}>Top 10</MenuItem>
                          <MenuItem value={25}>Top 25</MenuItem>
                          <MenuItem value={50}>Top 50</MenuItem>
                        </TextField>
                        <TextField
                          select
                          size="small"
                          value={sophiaCourseMonths}
                          onChange={(e) => setSophiaCourseMonths(Number(e.target.value))}
                          sx={{ minWidth: 140 }}
                        >
                          <MenuItem value={0}>All time</MenuItem>
                          <MenuItem value={3}>Last 3 months</MenuItem>
                          <MenuItem value={6}>Last 6 months</MenuItem>
                          <MenuItem value={12}>Last 12 months</MenuItem>
                          <MenuItem value={24}>Last 2 years</MenuItem>
                        </TextField>
                      </Box>
                    </Box>
                    <HighchartsReact highcharts={Highcharts} options={sophiaCoursesChartOptions} />
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
        </Container>
      </Box>
    </>
  );
}
